import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { 
  Building2, Upload, Trash2, Save, Loader2, Palette, Type, Image,
  CheckCircle, AlertCircle, RefreshCw, Sun, Moon, RotateCcw, Tags, Brain,
  FileText, Sparkles, Info, Zap, ExternalLink, Users, TrendingUp, MessageSquare
} from 'lucide-react';
import { useTheme, isValidHexColor, foregroundTokenFor } from '@/contexts/ThemeContext';
import api from '@/lib/api';
import { PageBanner } from '@/components/PageBanner';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { PageHeader } from '@/components/PageHeader';
import { useConfirm } from '@/components/ConfirmDialog';
import { LucideIconPicker, normalizeLucideIconName } from '@/components/LucideIconPicker';

// Matches the dim_tenants / dim_tenant_settings column defaults.
const DEFAULT_PRIMARY_COLOR = '#6366f1';
const DEFAULT_SECONDARY_COLOR = '#818cf8';

const defaultEvaPeopleConfig = {
  nudges: {
    enabled: true,
    privacy_threshold: 3,
    allow_social_proof: true,
    allow_trending: true,
    allow_gamification: true,
    allow_quick_tips: true,
  },
  quick_prompts: [
    { icon: 'ClipboardList', text: 'What are the PTO policies?', enabled: true },
    { icon: 'Receipt', text: 'How do I submit an expense report?', enabled: true },
    { icon: 'Hospital', text: 'What health insurance options are available?', enabled: true },
    { icon: 'House', text: 'What is the remote work policy?', enabled: true },
  ],
  trends: {
    enabled: true,
    max_topics: 5,
    minimum_conversations: 1,
  },
};
function mergeEvaPeopleConfig(config = {}) {
  return {
    ...defaultEvaPeopleConfig,
    ...config,
    nudges: {
      ...defaultEvaPeopleConfig.nudges,
      ...(config.nudges || {}),
    },
    quick_prompts: (config.quick_prompts || defaultEvaPeopleConfig.quick_prompts).map(prompt => ({
      ...prompt,
      icon: normalizeLucideIconName(prompt.icon),
    })),
    trends: {
      ...defaultEvaPeopleConfig.trends,
      ...(config.trends || {}),
    },
  };
}

function serializeEvaPeopleConfig(config) {
  return JSON.stringify(mergeEvaPeopleConfig(config));
}

function serializeSessionTimeoutConfig(config) {
  return JSON.stringify({
    default_timeout_minutes: config.default_timeout_minutes,
    available_options: [...(config.available_options || [])].sort((a, b) => a - b),
  });
}

function serializeModelConfig(config) {
  return JSON.stringify({
    fast_model: config.fast_model,
    thinking_model: config.thinking_model,
  });
}


export default function TenantSettingsPage() {
  const { user, tenant, hasRole, refreshTenant } = useAuth();
  const { theme, setTheme: setGlobalTheme } = useTheme();
  const fileInputRef = useRef(null);
  const [ConfirmEl, confirm] = useConfirm();

  const [settings, setSettings] = useState({
    display_name: '',
    shortname: '',
    primary_color: DEFAULT_PRIMARY_COLOR,
    secondary_color: DEFAULT_SECONDARY_COLOR,
    logo_url: null,
    company_description: null,
    long_description: null,
    industry: null,
    year_founded: null,
    company_type: null,
    employee_count: null,
    company_links: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState('organization');

  const [evaPeopleConfig, setEvaPeopleConfig] = useState(defaultEvaPeopleConfig);
  const [savingEvaPeopleConfig, setSavingEvaPeopleConfig] = useState(false);
  const savingEvaPeopleRef = useRef(false);
  const [evaPeopleConfigLoaded, setEvaPeopleConfigLoaded] = useState(false);
  const [evaPeopleLoadError, setEvaPeopleLoadError] = useState('');
  const [evaPeopleSaveError, setEvaPeopleSaveError] = useState('');
  const [evaPeopleSaved, setEvaPeopleSaved] = useState(false);
  const [validatePrompts, setValidatePrompts] = useState(false);
  const promptInputRefs = useRef([]);
  const [openQuickPromptIconPicker, setOpenQuickPromptIconPicker] = useState(null);
  const evaPeopleBaselineRef = useRef(JSON.stringify(defaultEvaPeopleConfig));

  // Theme customizer state
  const [themeConfig, setThemeConfig] = useState(null);
  const [savingTheme, setSavingTheme] = useState(false);

  // Topic Classification state
  const [topicConfig, setTopicConfig] = useState(null);
  const [topicModes, setTopicModes] = useState([]);
  const [savingTopicConfig, setSavingTopicConfig] = useState(false);

  // Session Timeout state
  const [sessionTimeoutConfig, setSessionTimeoutConfig] = useState({
    default_timeout_minutes: 10,
    available_options: [10, 30, 60]
  });
  const sessionTimeoutBaselineRef = useRef(JSON.stringify({
    default_timeout_minutes: 10,
    available_options: [10, 30, 60]
  }));
  const [newTimeoutOption, setNewTimeoutOption] = useState('');

  // AI Model Configuration state
  const [modelConfig, setModelConfig] = useState({
    fast_model: 'claude-sonnet-4-6',
    thinking_model: 'claude-opus-4-6',
  });
  const [availableModels, setAvailableModels] = useState({});
  const modelBaselineRef = useRef(JSON.stringify({
    fast_model: 'claude-sonnet-4-6',
    thinking_model: 'claude-opus-4-6',
  }));

  // Brand refresh state
  const [refreshingBrand, setRefreshingBrand] = useState(false);

  const isAdmin = hasRole(['admin']);
  const canViewOrganizationSettings = hasRole(['admin', 'approver']);
  const isSuperAdmin = hasRole(['superadmin']) || user?.platform_role === 'superadmin';
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const isEvaPeopleLandingDirty = serializeEvaPeopleConfig(evaPeopleConfig) !== evaPeopleBaselineRef.current;
  const isSessionTimeoutDirty = serializeSessionTimeoutConfig(sessionTimeoutConfig) !== sessionTimeoutBaselineRef.current;
  const isModelConfigDirty = serializeModelConfig(modelConfig) !== modelBaselineRef.current;
  const isEvaPeopleTabDirty = isEvaPeopleLandingDirty || isSessionTimeoutDirty || (isSuperAdmin && isModelConfigDirty);
  const promptErrors = evaPeopleConfig.quick_prompts.map(prompt => {
    const length = prompt.text.trim().length;
    return length < 3 || length > 160 ? 'Enter 3 to 160 characters, or remove this prompt.' : '';
  });
  const enabledPromptCount = evaPeopleConfig.quick_prompts.filter(prompt => prompt.enabled).length;

  useEffect(() => {
    fetchSettings();
    fetchThemeConfig();
    fetchTopicClassificationConfig();
    fetchSessionTimeoutConfig();
    fetchModelConfig();
    fetchEvaPeopleConfig();
  }, []);

  useEffect(() => {
    setLogoLoadError(false);
  }, [settings.logo_url]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isEvaPeopleTabDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEvaPeopleTabDirty]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings/tenant');
      setSettings({
        display_name: response.data.display_name || '',
        shortname: response.data.shortname || '',
        primary_color: response.data.primary_color || '#6366f1',
        secondary_color: response.data.secondary_color || '#818cf8',
        logo_url: response.data.logo_url || null,
        company_description: response.data.company_description || null,
        long_description: response.data.long_description || null,
        industry: response.data.industry || null,
        year_founded: response.data.year_founded || null,
        company_type: response.data.company_type || null,
        employee_count: response.data.employee_count || null,
        company_links: response.data.company_links || [],
      });
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      toast.error('Failed to load tenant settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchThemeConfig = async () => {
    try {
      const response = await api.get('/settings/tenant/theme');
      setThemeConfig(response.data.theme_config || {});
    } catch (err) {
      console.error('Failed to fetch theme config:', err);
      setThemeConfig({});
    }
  };

  const handleSaveTheme = async () => {
    const invalid = ['primary', 'secondary']
      .filter(key => themeConfig?.[key] && !isValidHexColor(themeConfig[key]));
    if (invalid.length) {
      toast.error('Enter a valid hex colour, e.g. #6366f1');
      return;
    }

    setSavingTheme(true);
    try {
      await api.put('/settings/tenant/theme', themeConfig);

      // No hand-rolled CSS variables here: refreshTenant() re-reads /auth/me,
      // which sources brand colours from dim_tenant_settings, and useBrandColors
      // repaints from that. Poking the variables directly is what made the theme
      // look applied while the stored colour was somewhere nothing read (EIP-298).
      await refreshTenant();

      // Keep the Organization tab's read-only swatches in step.
      setSettings(prev => ({
        ...prev,
        primary_color: themeConfig?.primary || prev.primary_color,
        secondary_color: themeConfig?.secondary || prev.secondary_color,
      }));

      toast.success('Theme saved and applied!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save theme');
    } finally {
      setSavingTheme(false);
    }
  };

  const handleResetTheme = () => {
    // Send the actual defaults rather than {} — an empty payload writes no
    // colours at all, so Reset appeared to do nothing.
    setThemeConfig({ primary: DEFAULT_PRIMARY_COLOR, secondary: DEFAULT_SECONDARY_COLOR });
    toast.info('Theme reset to defaults. Click Save to apply.');
  };

  // Topic Classification functions
  const fetchTopicClassificationConfig = async () => {
    try {
      const response = await api.get('/settings/topic-classification');
      setTopicConfig(response.data.config);
      setTopicModes(response.data.session_modes || []);
    } catch (err) {
      console.error('Failed to fetch topic config:', err);
    }
  };

  const handleSaveTopicConfig = async () => {
    setSavingTopicConfig(true);
    try {
      await api.put('/settings/topic-classification', topicConfig);
      toast.success('Topic classification settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save topic settings');
    } finally {
      setSavingTopicConfig(false);
    }
  };

  const handleResetTopicConfig = async () => {
    try {
      await api.post('/settings/topic-classification/reset');
      await fetchTopicClassificationConfig();
      toast.success('Topic settings reset to defaults');
    } catch (_err) {
      toast.error('Failed to reset settings');
    }
  };

  // Session Timeout functions
  const fetchSessionTimeoutConfig = async () => {
    try {
      const response = await api.get('/settings/session-timeout');
      const nextConfig = {
        default_timeout_minutes: response.data.default_timeout_minutes || 10,
        available_options: response.data.available_options || [10, 30, 60]
      };
      setSessionTimeoutConfig(nextConfig);
      sessionTimeoutBaselineRef.current = serializeSessionTimeoutConfig(nextConfig);
    } catch (err) {
      console.error('Failed to fetch session timeout config:', err);
    }
  };

  // AI Model Configuration functions
  const fetchModelConfig = async () => {
    try {
      const response = await api.get('/settings/models');
      const nextConfig = {
        fast_model: response.data.fast_model || 'claude-sonnet-4-6',
        thinking_model: response.data.thinking_model || 'claude-opus-4-6',
      };
      setModelConfig(nextConfig);
      modelBaselineRef.current = serializeModelConfig(nextConfig);
      setAvailableModels(response.data.available_models || {});
    } catch (err) {
      console.error('Failed to fetch model config:', err);
    }
  };

  const fetchEvaPeopleConfig = async () => {
    setEvaPeopleConfigLoaded(false);
    setEvaPeopleLoadError('');
    try {
      const response = await api.get('/settings/eva-people');
      const nextConfig = mergeEvaPeopleConfig(response.data.config || {});
      setEvaPeopleConfig(nextConfig);
      evaPeopleBaselineRef.current = serializeEvaPeopleConfig(nextConfig);
      setEvaPeopleConfigLoaded(true);
    } catch (err) {
      console.error('Failed to fetch Eva People config:', err);
      setEvaPeopleLoadError('Eva People settings could not load. Retry before making changes.');
    }
  };

  const handleSaveEvaPeopleConfig = async () => {
    if (savingEvaPeopleRef.current || !evaPeopleConfigLoaded || !(isAdmin || isSuperAdmin)) return;
    setValidatePrompts(true);
    setEvaPeopleSaveError('');
    setEvaPeopleSaved(false);
    if (promptErrors.some(Boolean)) {
      promptInputRefs.current[promptErrors.findIndex(Boolean)]?.focus();
      return;
    }
    if (enabledPromptCount > 4) {
      setEvaPeopleSaveError('Enable at most 4 prompts. Turn off a prompt before saving.');
      return;
    }
    if (!isEvaPeopleTabDirty) return;
    savingEvaPeopleRef.current = true;
    setSavingEvaPeopleConfig(true);
    const normalizedEvaPeopleConfig = mergeEvaPeopleConfig({
      ...evaPeopleConfig,
      quick_prompts: evaPeopleConfig.quick_prompts.map(prompt => ({ ...prompt, text: prompt.text.trim() })),
    });
    const failures = [];
    const saveError = (err, fallback) => {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      return status >= 400 && status < 500 && typeof detail === 'string'
        ? detail.replace(/[.!]+$/, '') : fallback;
    };

    // EIP-321: three independent writes used to share one try/catch. A
    // failure in a later write (e.g. session-timeout) reported "Failed to
    // save Eva People settings" even though the eva-people write — including
    // e.g. a Social proof toggle flip — had already committed. That produced
    // exactly the reported symptom: a toggle that saved but appeared not to.
    // Each write now owns its own try/catch and its own baseline update.
    if (isEvaPeopleLandingDirty) try {
      await api.put('/settings/eva-people', normalizedEvaPeopleConfig);
      setEvaPeopleConfig(normalizedEvaPeopleConfig);
      evaPeopleBaselineRef.current = serializeEvaPeopleConfig(normalizedEvaPeopleConfig);
    } catch (err) {
      failures.push(saveError(err, 'starter prompts and activity settings'));
    }

    if (isSessionTimeoutDirty) try {
      await api.put('/settings/session-timeout', sessionTimeoutConfig);
      sessionTimeoutBaselineRef.current = serializeSessionTimeoutConfig(sessionTimeoutConfig);
    } catch (err) {
      failures.push(saveError(err, 'session timeout settings'));
    }

    if (isSuperAdmin && isModelConfigDirty) {
      try {
        await api.put('/settings/models', modelConfig);
        modelBaselineRef.current = serializeModelConfig(modelConfig);
      } catch (err) {
        failures.push(saveError(err, 'response settings'));
      }
    }

    setSavingEvaPeopleConfig(false);
    savingEvaPeopleRef.current = false;
    if (failures.length === 0) {
      setEvaPeopleSaved(true);
      toast.success('Eva People settings saved');
    } else {
      const message = `Could not save: ${failures.join(', ')}. Your changes are still here. Try saving again.`;
      setEvaPeopleSaveError(message);
      toast.error(message);
    }
  };

  const handleTabChange = async (nextTab) => {
    if (savingEvaPeopleRef.current) return;
    if (activeTab === 'people' && nextTab !== activeTab && isEvaPeopleTabDirty) {
      const ok = await confirm({
        title: 'Unsaved Eva People Changes',
        description: 'You have unsaved Eva People configuration changes. Leave this tab without saving?',
        confirmLabel: 'Leave Without Saving',
        variant: 'warning',
      });
      if (!ok) return;
      setEvaPeopleConfig(JSON.parse(evaPeopleBaselineRef.current));
      setSessionTimeoutConfig(JSON.parse(sessionTimeoutBaselineRef.current));
      setModelConfig(JSON.parse(modelBaselineRef.current));
      setValidatePrompts(false);
      setEvaPeopleSaveError('');
    }
    setActiveTab(nextTab);
  };

  const updateQuickPrompt = (index, field, value) => {
    setEvaPeopleConfig(prev => ({
      ...prev,
      quick_prompts: prev.quick_prompts.map((prompt, idx) => (
        idx === index ? { ...prompt, [field]: value } : prompt
      )),
    }));
  };

  const addQuickPrompt = () => {
    if ((evaPeopleConfig.quick_prompts || []).length >= 8) {
      toast.error('At most 8 quick prompts can be configured');
      return;
    }
    setEvaPeopleConfig(prev => ({
      ...prev,
      quick_prompts: [...(prev.quick_prompts || []), { icon: 'MessageSquare', text: '', enabled: false }],
    }));
  };

  const removeQuickPrompt = (index) => {
    setEvaPeopleConfig(prev => ({
      ...prev,
      quick_prompts: prev.quick_prompts.filter((_, idx) => idx !== index),
    }));
  };

  // Refresh public company profile data
  const handleRefreshBrand = async () => {
    setRefreshingBrand(true);
    try {
      // Get domain from email_domains in tenant - use shortname as domain hint
      const response = await api.post('/settings/tenant/refresh-brand', {
        domain: null  // Will use tenant's email_domains
      });
      
      // Update local settings with new brand info
      const brandInfo = response.data.brand_info;
      setSettings(prev => ({
        ...prev,
        logo_url: brandInfo.logo_url || prev.logo_url,
        icon_url: brandInfo.icon_url || prev.icon_url,
        primary_color: brandInfo.primary_color || prev.primary_color,
        secondary_color: brandInfo.secondary_color || prev.secondary_color,
        company_description: brandInfo.description || brandInfo.long_description,
        long_description: brandInfo.long_description,
        industry: brandInfo.industry,
        year_founded: brandInfo.year_founded,
        company_type: brandInfo.company_type,
        employee_count: brandInfo.employee_count,
        company_links: brandInfo.links || [],
      }));
      
      toast.success('Company profile updated');
      // Refresh tenant context to update logo in sidebar
      await refreshTenant();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to refresh company profile');
    } finally {
      setRefreshingBrand(false);
    }
  };

  const addTimeoutOption = () => {
    const minutes = parseInt(newTimeoutOption);
    if (isNaN(minutes) || minutes < 1 || minutes > 480) {
      toast.error('Please enter a valid number between 1 and 480');
      return;
    }
    if (sessionTimeoutConfig.available_options.includes(minutes)) {
      toast.error('This option already exists');
      return;
    }
    setSessionTimeoutConfig(prev => ({
      ...prev,
      available_options: [...prev.available_options, minutes].sort((a, b) => a - b)
    }));
    setNewTimeoutOption('');
  };

  const removeTimeoutOption = (minutes) => {
    if (sessionTimeoutConfig.available_options.length <= 1) {
      toast.error('At least one option is required');
      return;
    }
    const newOptions = sessionTimeoutConfig.available_options.filter(opt => opt !== minutes);
    // If we removed the default, set new default to first option
    const newDefault = newOptions.includes(sessionTimeoutConfig.default_timeout_minutes)
      ? sessionTimeoutConfig.default_timeout_minutes
      : newOptions[0];
    setSessionTimeoutConfig({
      available_options: newOptions,
      default_timeout_minutes: newDefault
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Colours are deliberately NOT sent: they are read-only here and owned by
      // the Theme tab. Re-asserting this tab's stale copy silently reverted the
      // saved brand colour (EIP-298).
      await api.put('/settings/tenant', {
        display_name: settings.display_name,
        shortname: settings.shortname,
      });
      await refreshTenant();
      toast.success('Settings saved successfully.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview
    setLogoLoadError(false);
    const reader = new FileReader();
    reader.onloadend = () => setPreviewLogo(reader.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/settings/tenant/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      setPreviewLogo(null);
      toast.success('Logo uploaded successfully');
      // Refresh tenant context to update logo in sidebar
      await refreshTenant();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
      setPreviewLogo(null);
    } finally {
      setUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    const ok = await confirm({ title: 'Delete Logo', description: 'Are you sure you want to delete the logo?', confirmLabel: 'Delete', variant: 'delete' });
    if (!ok) return;
    
    try {
      await api.delete('/settings/tenant/logo');
      setSettings(prev => ({ ...prev, logo_url: null }));
      toast.success('Logo deleted successfully');
      // Refresh tenant context to update logo in sidebar
      await refreshTenant();
    } catch (_err) {
      toast.error('Failed to delete logo');
    }
  };

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        banner
        bannerPageKey="settings"
        tabs
        sections={2}
        sectionClassName="h-72"
        data-testid="tenant-settings-page"
      />
    );
  }

  // Handle both absolute logo URLs and relative URLs from upload
  const getLogoUrl = (url) => {
    if (!url) return null;
    // If it's already an absolute URL (http:// or https://), use as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Otherwise, prepend API_BASE for relative URLs
    return `${API_BASE}${url}`;
  };
  
  const logoSrc = previewLogo || getLogoUrl(settings.logo_url);

  if (!(canViewOrganizationSettings || isSuperAdmin)) {
    return (
      <PageShell maxWidth="7xl">
        <Card className="bg-warning/5 border-warning/30">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertCircle className="h-8 w-8 text-warning" />
            <div>
              <h3 className="font-semibold">Access Denied</h3>
              <p className="text-muted-foreground">Only approvers and administrators can view organization settings.</p>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="7xl" data-testid="tenant-settings-page">
      {ConfirmEl}
      {/* Page Banner */}
      <PageBanner pageKey="settings" />

      <PageHeader
        title="Organization Settings"
        icon={Building2}
        description="Customize your organization's branding and appearance"
        actions={(
          <Badge variant="outline" className="w-fit">
          Tenant: {tenant?.tenant_id || user?.tenant_id}
          </Badge>
        )}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className={`grid w-full max-w-3xl ${isSuperAdmin ? 'grid-cols-4' : 'grid-cols-3'}`}>
          <TabsTrigger value="organization" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="theme" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Theme
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="topics" className="flex items-center gap-2">
              <Tags className="h-4 w-4" />
              Topics
            </TabsTrigger>
          )}
          <TabsTrigger value="people" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Eva People
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organization" className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Logo Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-primary" />
              Organization Logo
            </CardTitle>
            <CardDescription>Upload your organization&apos;s logo (PNG, JPG, SVG, WebP)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-6">
              {/* Logo Preview */}
              <div 
                className="w-24 h-24 rounded-xl flex items-center justify-center border-2 border-dashed border-border overflow-hidden"
                style={{ backgroundColor: settings.primary_color + '20' }}
              >
                {logoSrc && !logoLoadError ? (
                  <img
                    src={logoSrc}
                    alt="Logo"
                    className="w-full h-full object-contain p-2"
                    onError={() => setLogoLoadError(true)}
                  />
                ) : (
                  <span 
                    className="text-4xl font-bold"
                    style={{ color: settings.primary_color }}
                  >
                    {settings.display_name?.charAt(0) || 'O'}
                  </span>
                )}
              </div>

              {/* Upload/Delete Buttons */}
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  disabled={!isAdmin}
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!isAdmin || uploading}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="upload-logo-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload Logo
                </Button>
                {settings.logo_url && (
                  <Button
                    variant="outline"
                    onClick={handleLogoDelete}
                    disabled={!isAdmin}
                    className="text-destructive border-destructive/30 hover:bg-destructive/5"
                    data-testid="delete-logo-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Logo
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Recommended: Square image, at least 200x200px</p>
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Company Info
                </CardTitle>
                <CardDescription>Company profile and public organization details</CardDescription>
              </div>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshBrand}
                  disabled={refreshingBrand}
                  className="shrink-0"
                  data-testid="refresh-brand-btn"
                >
                  {refreshingBrand ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Refresh Profile
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings.company_description || settings.industry || settings.year_founded || settings.company_type ? (
              <div className="space-y-3">
                {settings.company_description && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">About</p>
                    <p className="text-sm text-foreground line-clamp-3">{settings.company_description}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  {settings.industry && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Industry</p>
                      <p className="text-sm font-medium text-foreground">{settings.industry}</p>
                    </div>
                  )}
                  {settings.year_founded && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Founded</p>
                      <p className="text-sm font-medium text-foreground">{settings.year_founded}</p>
                    </div>
                  )}
                  {settings.company_type && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Type</p>
                      <p className="text-sm font-medium text-foreground capitalize">
                        {settings.company_type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                      </p>
                    </div>
                  )}
                  {settings.employee_count && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Employees</p>
                      <p className="text-sm font-medium text-foreground">
                        {(() => {
                          // Convert employee count to human-readable range
                          const count = parseInt(settings.employee_count, 10);
                          if (isNaN(count)) return settings.employee_count; // Already a string
                          if (count === 1) return '1 employee';
                          if (count <= 10) return '2-10 employees';
                          if (count <= 50) return '11-50 employees';
                          if (count <= 200) return '51-200 employees';
                          if (count <= 500) return '201-500 employees';
                          if (count <= 1000) return '501-1,000 employees';
                          if (count <= 5000) return '1,001-5,000 employees';
                          if (count <= 10000) return '5,001-10,000 employees';
                          return '10,001+ employees';
                        })()}
                      </p>
                    </div>
                  )}
                </div>
                {settings.company_links && settings.company_links.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Links</p>
                    <div className="flex flex-wrap gap-2">
                      {settings.company_links.map((link, idx) => {
                        // Get appropriate icon based on link name/url
                        const linkName = (link.name || '').toLowerCase();
                        const linkUrl = (link.url || '').toLowerCase();
                        
                        let Icon = ExternalLink;
                        let bgColor = 'bg-muted';
                        let hoverColor = 'hover:bg-muted/80';
                        
                        if (linkName.includes('linkedin') || linkUrl.includes('linkedin')) {
                          bgColor = 'bg-[#0077B5]/10';
                          hoverColor = 'hover:bg-[#0077B5]/20';
                        } else if (linkName.includes('twitter') || linkUrl.includes('twitter') || linkUrl.includes('x.com')) {
                          bgColor = 'bg-black/10 dark:bg-white/10';
                          hoverColor = 'hover:bg-black/20 dark:hover:bg-white/20';
                        } else if (linkName.includes('facebook') || linkUrl.includes('facebook')) {
                          bgColor = 'bg-[#1877F2]/10';
                          hoverColor = 'hover:bg-[#1877F2]/20';
                        } else if (linkName.includes('instagram') || linkUrl.includes('instagram')) {
                          bgColor = 'bg-[#E4405F]/10';
                          hoverColor = 'hover:bg-[#E4405F]/20';
                        } else if (linkName.includes('youtube') || linkUrl.includes('youtube')) {
                          bgColor = 'bg-[#FF0000]/10';
                          hoverColor = 'hover:bg-[#FF0000]/20';
                        } else if (linkName.includes('github') || linkUrl.includes('github')) {
                          bgColor = 'bg-black/10 dark:bg-white/10';
                          hoverColor = 'hover:bg-black/20 dark:hover:bg-white/20';
                        }
                        
                        return (
                          <a 
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${bgColor} ${hoverColor}`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {link.name || 'Website'}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                <Info className="w-8 h-8 mb-2 opacity-50" />
                <p className="text-sm font-medium">No company profile data available</p>
                <p className="text-xs text-center mt-1">Refresh to look up public company details, or enter them manually.</p>
              </div>
            )}

            {/* Brand Color Preview */}
            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Brand Colors</p>
              <div className="flex items-center gap-3">
                <div 
                  className="w-8 h-8 rounded-full border-2 border-border"
                  style={{ backgroundColor: settings.primary_color }}
                  title="Primary Color"
                />
                <div 
                  className="w-8 h-8 rounded-full border-2 border-border"
                  style={{ backgroundColor: settings.secondary_color }}
                  title="Secondary Color"
                />
                <span className="text-xs text-muted-foreground">Edit in Theme tab</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organization Details */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Type className="w-5 h-5 text-primary" />
              Organization Details
            </CardTitle>
            <CardDescription>Set your organization&apos;s name and identifier</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="display-name">Display Name</Label>
                <Input
                  id="display-name"
                  value={settings.display_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, display_name: e.target.value }))}
                  disabled={!isAdmin}
                  placeholder="Acme Corporation"
                  className="mt-1"
                  data-testid="display-name-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Shown in the sidebar and headers</p>
              </div>
              <div>
                <Label htmlFor="shortname">Short Name</Label>
                <Input
                  id="shortname"
                  value={settings.shortname}
                  onChange={(e) => setSettings(prev => ({ ...prev, shortname: e.target.value }))}
                  disabled={!isAdmin}
                  placeholder="acme"
                  className="mt-1"
                  data-testid="shortname-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Used for internal references</p>
              </div>
            </div>

            {isAdmin && (
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-settings-btn">
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-info/5 border-info/30">
        <CardContent className="p-4 flex items-start gap-3">
          <RefreshCw className="w-5 h-5 text-info mt-0.5" />
          <div>
            <p className="font-medium text-info">Changes require re-login</p>
            <p className="text-sm text-info/80">After saving settings, log out and back in to see your branding changes reflected across the application.</p>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Theme Tab */}
        <TabsContent value="theme" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-primary" />
                Theme Customization
              </CardTitle>
              <CardDescription>Customize the appearance of your organization&apos;s dashboard</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme Mode Toggle */}
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div>
                  <p className="font-medium">Color Mode</p>
                  <p className="text-sm text-muted-foreground">Switch between light and dark themes</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={theme === 'light' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setGlobalTheme('light')}
                    data-testid="light-mode-btn"
                  >
                    <Sun className="h-4 w-4 mr-2" />
                    Light
                  </Button>
                  <Button
                    variant={theme === 'dark' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setGlobalTheme('dark')}
                    data-testid="dark-mode-btn"
                  >
                    <Moon className="h-4 w-4 mr-2" />
                    Dark
                  </Button>
                </div>
              </div>

              {/* Custom Colors */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Custom Colors</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="theme-primary">Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        id="theme-primary"
                        value={themeConfig?.primary || settings.primary_color}
                        onChange={(e) => setThemeConfig(prev => ({ ...prev, primary: e.target.value }))}
                        className="w-12 h-10 p-1 cursor-pointer"
                        disabled={!isAdmin}
                      />
                      <Input
                        value={themeConfig?.primary || settings.primary_color}
                        onChange={(e) => setThemeConfig(prev => ({ ...prev, primary: e.target.value }))}
                        placeholder="#6366f1"
                        disabled={!isAdmin}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    {/* Writes secondary_color, which drives Eva People's chat
                        send-button gradient. It was labelled "Accent" while
                        writing `secondary`, so nobody could tell what it did. */}
                    <Label htmlFor="theme-secondary">Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        id="theme-secondary"
                        value={themeConfig?.secondary || settings.secondary_color}
                        onChange={(e) => setThemeConfig(prev => ({ ...prev, secondary: e.target.value }))}
                        className="w-12 h-10 p-1 cursor-pointer"
                        disabled={!isAdmin}
                      />
                      <Input
                        value={themeConfig?.secondary || settings.secondary_color}
                        onChange={(e) => setThemeConfig(prev => ({ ...prev, secondary: e.target.value }))}
                        placeholder="#818cf8"
                        disabled={!isAdmin}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Preview</h4>
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-full"
                      style={{ backgroundColor: themeConfig?.primary || settings.primary_color }}
                    />
                    <div
                      className="w-8 h-8 rounded-full"
                      style={{ backgroundColor: themeConfig?.secondary || settings.secondary_color }}
                    />
                    <span className="text-sm text-muted-foreground">Your brand colors</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      style={{
                        backgroundColor: themeConfig?.primary || settings.primary_color,
                        // Mirrors what the app will actually render, so the
                        // preview doesn't lie for light brand colours.
                        color: `hsl(${foregroundTokenFor(themeConfig?.primary || settings.primary_color) || '210 40% 98%'})`
                      }}
                    >
                      Primary Button
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      style={{ borderColor: themeConfig?.secondary || settings.secondary_color }}
                    >
                      Secondary Button
                    </Button>
                  </div>
                </div>
              </div>

              {isAdmin && (
                <div className="flex justify-between pt-4 border-t">
                  <Button variant="outline" onClick={handleResetTheme}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset to Defaults
                  </Button>
                  <Button onClick={handleSaveTheme} disabled={savingTheme} className="bg-primary text-primary-foreground hover:bg-primary/90">
                    {savingTheme ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Theme
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Topic Classification Tab */}
        <TabsContent value="topics" className="space-y-6">
          {topicConfig && (
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Session Classification */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-primary" />
                    Session Classification
                  </CardTitle>
                  <CardDescription>
                    Configure how topics are extracted from chat conversations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Classification Mode */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Classification Mode</Label>
                    <div className="space-y-2">
                      {topicModes.map((mode) => (
                        <div
                          key={mode.value}
                          onClick={() => setTopicConfig({ ...topicConfig, session_mode: mode.value })}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            topicConfig.session_mode === mode.value
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">{mode.label}</span>
                            {topicConfig.session_mode === mode.value && (
                              <CheckCircle className="w-4 h-4 text-primary" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{mode.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Semantic Weight (only for hybrid) */}
                  {topicConfig.session_mode === 'hybrid' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Semantic vs Document Weight</Label>
                        <span className="text-xs text-muted-foreground">
                          {Math.round(topicConfig.semantic_weight * 100)}% semantic / {Math.round((1 - topicConfig.semantic_weight) * 100)}% docs
                        </span>
                      </div>
                      <Slider
                        value={[topicConfig.semantic_weight * 100]}
                        onValueChange={([v]) => setTopicConfig({ ...topicConfig, semantic_weight: v / 100 })}
                        min={0}
                        max={100}
                        step={10}
                      />
                    </div>
                  )}

                  {/* Max Topics */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Max Topics</Label>
                      <Input
                        type="number"
                        min={1}
                        max={5}
                        value={topicConfig.max_topics}
                        onChange={(e) => setTopicConfig({ ...topicConfig, max_topics: parseInt(e.target.value) || 3 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Max Sub-Topics</Label>
                      <Input
                        type="number"
                        min={1}
                        max={3}
                        value={topicConfig.max_sub_topics_per_topic}
                        onChange={(e) => setTopicConfig({ ...topicConfig, max_sub_topics_per_topic: parseInt(e.target.value) || 2 })}
                      />
                    </div>
                  </div>

                  {/* Emerging Topics */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm font-medium">Allow Emerging Topics</Label>
                        <p className="text-xs text-muted-foreground">Suggest new topics not in taxonomy</p>
                      </div>
                      <Switch
                        checked={topicConfig.allow_emerging_topics}
                        onCheckedChange={(v) => setTopicConfig({ ...topicConfig, allow_emerging_topics: v })}
                      />
                    </div>

                    {topicConfig.allow_emerging_topics && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Confidence Threshold</Label>
                          <span className="text-xs text-muted-foreground">{topicConfig.emerging_topic_threshold}</span>
                        </div>
                        <Slider
                          value={[topicConfig.emerging_topic_threshold * 100]}
                          onValueChange={([v]) => setTopicConfig({ ...topicConfig, emerging_topic_threshold: v / 100 })}
                          min={50}
                          max={95}
                          step={5}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Document Classification */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Document Classification
                  </CardTitle>
                  <CardDescription>
                    Configure how topics are assigned to uploaded KB documents
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Auto-classify */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-5 h-5 text-primary" />
                      <div>
                        <Label className="text-sm font-medium">Auto-classify on Upload</Label>
                        <p className="text-xs text-muted-foreground">Use AI to suggest topics for new documents</p>
                      </div>
                    </div>
                    <Switch
                      checked={topicConfig.document_auto_classify}
                      onCheckedChange={(v) => setTopicConfig({ ...topicConfig, document_auto_classify: v })}
                    />
                  </div>

                  {topicConfig.document_auto_classify && (
                    <>
                      {/* Max Topics for Docs */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm">Max Topics</Label>
                          <Input
                            type="number"
                            min={1}
                            max={3}
                            value={topicConfig.document_max_topics}
                            onChange={(e) => setTopicConfig({ ...topicConfig, document_max_topics: parseInt(e.target.value) || 2 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Max Sub-Topics</Label>
                          <Input
                            type="number"
                            min={1}
                            max={3}
                            value={topicConfig.document_max_sub_topics}
                            onChange={(e) => setTopicConfig({ ...topicConfig, document_max_sub_topics: parseInt(e.target.value) || 2 })}
                          />
                        </div>
                      </div>

                      {/* Allow Emerging for Docs */}
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium">Allow Emerging Topics</Label>
                          <p className="text-xs text-muted-foreground">Suggest new topics from document content</p>
                        </div>
                        <Switch
                          checked={topicConfig.document_allow_emerging}
                          onCheckedChange={(v) => setTopicConfig({ ...topicConfig, document_allow_emerging: v })}
                        />
                      </div>
                    </>
                  )}

                  {/* Info box */}
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                    <div className="flex gap-2">
                      <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        Document topics are suggestions only. Content team can review and modify before publishing.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="lg:col-span-2 flex justify-end gap-3">
                <Button variant="outline" onClick={handleResetTopicConfig}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset to Defaults
                </Button>
                <Button onClick={handleSaveTopicConfig} disabled={savingTopicConfig}>
                  {savingTopicConfig ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Settings
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Eva People Settings Tab */}
        <TabsContent value="people" className="space-y-6">
          {!evaPeopleConfigLoaded && (
            <div className="rounded-lg border border-border p-4" role={evaPeopleLoadError ? 'alert' : 'status'}>
              <p>{evaPeopleLoadError || 'Loading Eva People settings...'}</p>
              {evaPeopleLoadError && <Button variant="outline" className="mt-3 min-h-11" onClick={fetchEvaPeopleConfig}>Retry settings</Button>}
            </div>
          )}
          {!(isAdmin || isSuperAdmin) && <p className="text-sm text-muted-foreground">Only administrators can change Eva People settings.</p>}
          <fieldset disabled={!evaPeopleConfigLoaded || savingEvaPeopleConfig || !(isAdmin || isSuperAdmin)} className="min-w-0 space-y-6">
          {isEvaPeopleTabDirty && (
            <Card className="border-warning/40 bg-warning/5">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-warning" />
                  <div>
                    <p className="font-medium">Unsaved Eva People changes</p>
                    <p className="text-sm text-muted-foreground">
                      Save before leaving this tab to apply landing page, session, and response settings.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Employee Activity and Privacy
              </CardTitle>
              <CardDescription>
                Controls the aggregate organization activity employees can see in Eva People,
                including nudges, trending topics and the live activity ticker, and the minimum
                group size for showing it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <Label className="font-medium">Show dynamic nudges</Label>
                  <p className="text-sm text-muted-foreground">Use real aggregate usage, topic, and gamification signals.</p>
                </div>
                <Switch
                  checked={evaPeopleConfig.nudges.enabled}
                  onCheckedChange={(v) => setEvaPeopleConfig(prev => ({ ...prev, nudges: { ...prev.nudges, enabled: v } }))}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block" htmlFor="privacy-threshold-input">Privacy threshold</Label>
                  <Input
                    id="privacy-threshold-input"
                    data-testid="privacy-threshold-input"
                    aria-label="Privacy threshold"
                    type="number"
                    min={3}
                    max={100}
                    value={evaPeopleConfig.nudges.privacy_threshold}
                    onChange={(e) => setEvaPeopleConfig(prev => ({ ...prev, nudges: { ...prev.nudges, privacy_threshold: parseInt(e.target.value) || 3 } }))}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    The minimum number of distinct employees behind any aggregate Eva People
                    shows. Applies to social-proof nudges, the Trending this week topics, and the
                    live activity ticker — none of them appear until at least this many people
                    are counted.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['allow_social_proof', 'Social proof'],
                    ['allow_trending', 'Trending topics'],
                    ['allow_gamification', 'Gamification'],
                    ['allow_quick_tips', 'Quick tips'],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 rounded-lg border border-border p-3 text-sm">
                      <Switch
                        aria-label={label}
                        data-testid={`nudge-toggle-${key}`}
                        checked={evaPeopleConfig.nudges[key]}
                        onCheckedChange={(v) => setEvaPeopleConfig(prev => ({ ...prev, nudges: { ...prev.nudges, [key]: v } }))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground md:col-span-2">
                  Social proof also controls the live activity ticker at the top of the Eva
                  People chat. Turning it off hides the ticker for employees and suppresses
                  every social nudge.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card id="starter-prompts">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Quick Prompts
              </CardTitle>
              <CardDescription>
                Choose up to 4 starter questions for employees. Keep up to 8 prompts, including disabled ones.
                Employees see saved changes when they open People or start a new chat.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(evaPeopleConfig.quick_prompts || []).map((prompt, index) => (
                <div key={index} className="grid min-w-0 gap-3 rounded-lg border border-border p-4 md:grid-cols-[160px_1fr_auto_auto] md:items-start">
                  <LucideIconPicker
                    value={prompt.icon}
                    onChange={(iconName) => updateQuickPrompt(index, 'icon', iconName)}
                    isOpen={openQuickPromptIconPicker === index}
                    onToggle={() => setOpenQuickPromptIconPicker(openQuickPromptIconPicker === index ? null : index)}
                    onClose={() => setOpenQuickPromptIconPicker(null)}
                    label={`Prompt ${index + 1} icon`}
                  />
                  <div className="min-w-0 space-y-1.5">
                  <Label htmlFor={`quick-prompt-${index}`}>Prompt {index + 1}</Label>
                  <Textarea
                    id={`quick-prompt-${index}`}
                    ref={element => { promptInputRefs.current[index] = element; }}
                    value={prompt.text}
                    onChange={(e) => updateQuickPrompt(index, 'text', e.target.value)}
                    placeholder="Prompt text"
                    aria-label={`Prompt ${index + 1} text`}
                    aria-invalid={validatePrompts && !!promptErrors[index]}
                    aria-describedby={`quick-prompt-help-${index}`}
                    maxLength={160}
                    rows={3}
                    className="min-h-20 resize-y"
                  />
                  <p id={`quick-prompt-help-${index}`} className={`text-xs ${validatePrompts && promptErrors[index] ? 'text-destructive' : 'text-muted-foreground'}`} role={validatePrompts && promptErrors[index] ? 'alert' : undefined}>
                    {validatePrompts && promptErrors[index] ? promptErrors[index] : `${prompt.text.length}/160 characters`}
                  </p>
                  </div>
                  <label className="flex min-h-11 items-center gap-2 text-sm">
                    <Switch
                      aria-label={`Enable prompt ${index + 1}`}
                      checked={prompt.enabled}
                      onCheckedChange={(v) => updateQuickPrompt(index, 'enabled', v)}
                    />
                    Enabled
                  </label>
                  <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => removeQuickPrompt(index)} aria-label={`Remove prompt ${index + 1}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <p className="text-sm text-muted-foreground">{enabledPromptCount} of 4 enabled · {evaPeopleConfig.quick_prompts.length} of 8 prompts</p>
              {evaPeopleConfig.quick_prompts.length === 0 && <p className="text-sm text-muted-foreground">No starter prompts. Employees can still type their own questions.</p>}
              <Button variant="outline" className="min-h-11" onClick={addQuickPrompt} disabled={evaPeopleConfig.quick_prompts.length >= 8}>
                Add Prompt
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Trends
              </CardTitle>
              <CardDescription>
                Control the trending topics shown on the Eva People landing page.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <Label className="font-medium">Show trends</Label>
                <Switch
                  checked={evaPeopleConfig.trends.enabled}
                  onCheckedChange={(v) => setEvaPeopleConfig(prev => ({ ...prev, trends: { ...prev.trends, enabled: v } }))}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Max topics</Label>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={evaPeopleConfig.trends.max_topics}
                  onChange={(e) => setEvaPeopleConfig(prev => ({ ...prev, trends: { ...prev.trends, max_topics: parseInt(e.target.value) || 0 } }))}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block" htmlFor="minimum-conversations-input">Minimum conversations</Label>
                <Input
                  id="minimum-conversations-input"
                  data-testid="minimum-conversations-input"
                  aria-label="Minimum conversations"
                  type="number"
                  min={0}
                  max={100}
                  value={evaPeopleConfig.trends.minimum_conversations}
                  onChange={(e) => setEvaPeopleConfig(prev => ({ ...prev, trends: { ...prev.trends, minimum_conversations: parseInt(e.target.value) || 0 } }))}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  A relevance filter: how busy a topic must be before employees see it trending.
                  It can only raise the bar — the privacy threshold above is always applied too.
                </p>
                {evaPeopleConfig.trends.minimum_conversations < evaPeopleConfig.nudges.privacy_threshold && (
                  <p className="mt-1 text-xs font-medium text-foreground" data-testid="trends-effective-minimum">
                    Effective minimum: {evaPeopleConfig.nudges.privacy_threshold} (privacy threshold)
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-primary" />
                Session Timeout Options
              </CardTitle>
              <CardDescription>
                Configure the inactivity timeout options available to employees. This affects when the assistant automatically summarizes conversations and extracts topics.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Options */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Available Timeout Options (minutes)</Label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {sessionTimeoutConfig.available_options.map((minutes) => (
                    <div 
                      key={minutes} 
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                        sessionTimeoutConfig.default_timeout_minutes === minutes
                          ? 'bg-primary/10 border-primary text-primary'
                          : 'bg-background border-border'
                      }`}
                    >
                      <span className="text-sm font-medium">{minutes} min</span>
                      {sessionTimeoutConfig.default_timeout_minutes === minutes && (
                        <Badge variant="secondary" className="text-xs">default</Badge>
                      )}
                      <button
                        onClick={() => removeTimeoutOption(minutes)}
                        className="p-0.5 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add New Option */}
              <div className="flex items-end gap-3">
                <div className="flex-1 max-w-[200px]">
                  <Label className="text-sm font-medium mb-1.5 block">Add Option</Label>
                  <Input
                    type="number"
                    min="1"
                    max="480"
                    value={newTimeoutOption}
                    onChange={(e) => setNewTimeoutOption(e.target.value)}
                    placeholder="Minutes (1-480)"
                    className="w-full"
                  />
                </div>
                <Button variant="outline" onClick={addTimeoutOption}>
                  Add
                </Button>
              </div>

              {/* Default Selection */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Default Timeout for New Users</Label>
                <div className="flex flex-wrap gap-2">
                  {sessionTimeoutConfig.available_options.map((minutes) => (
                    <button
                      key={minutes}
                      onClick={() => setSessionTimeoutConfig(prev => ({
                        ...prev,
                        default_timeout_minutes: minutes
                      }))}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                        sessionTimeoutConfig.default_timeout_minutes === minutes
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                      }`}
                    >
                      {minutes} min
                    </button>
                  ))}
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">How Session Timeout Works</p>
                    <p>After a period of inactivity, the assistant automatically summarizes the conversation and extracts topics for analytics. Users can choose their preferred timeout duration from the options you configure here.</p>
                    <p className="mt-2"><strong>Tip:</strong> For development/testing, use shorter timeouts (1-5 min). For production, longer timeouts (10-60 min) provide a better user experience.</p>
                  </div>
                </div>
              </div>

            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Response Modes
              </CardTitle>
              <CardDescription>
                Eva offers two response modes that employees can switch between during conversations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Standard Mode */}
              <div className="p-4 rounded-lg border border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Standard</p>
                    <p className="text-xs text-muted-foreground">Fast, efficient responses</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground ml-11">
                  Uses lighter AI models to search your knowledge base and answer questions. Great for most everyday queries — policy lookups, FAQs, and common requests. Responds quickly with lower cost per conversation.
                </p>
              </div>

              {/* Advanced Mode */}
              <div className="p-4 rounded-lg border border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Brain className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Advanced</p>
                    <p className="text-xs text-muted-foreground">More capable, higher quality</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground ml-11">
                  Uses larger AI models for both research and orchestration. Better at complex questions that span multiple topics, require nuanced interpretation, or involve comparing information across documents. Takes longer and uses more tokens.
                </p>
              </div>

              {/* Info box */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p>Employees can switch between Standard and Advanced modes in the Eva People chat interface. Standard is used by default.</p>
                  </div>
                </div>
              </div>

              {/* Superadmin-only: model selection */}
              {isSuperAdmin && (
                <>
                  <div className="pt-4 border-t">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Model Configuration (Superadmin)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Standard Mode</Label>
                        <div className="grid gap-2">
                          {Object.entries(availableModels).map(([_provider, models]) => (
                            models.map(model => (
                              <button
                                key={model.id}
                                onClick={() => setModelConfig(prev => ({ ...prev, fast_model: model.id }))}
                                className={`p-2 rounded-lg border text-left transition-all text-xs ${
                                  modelConfig.fast_model === model.id
                                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                }`}
                              >
                                <span className="font-medium">{model.name}</span>
                              </button>
                            ))
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Advanced Mode</Label>
                        <div className="grid gap-2">
                          {Object.entries(availableModels).map(([_provider, models]) => (
                            models.map(model => (
                              <button
                                key={model.id}
                                onClick={() => setModelConfig(prev => ({ ...prev, thinking_model: model.id }))}
                                className={`p-2 rounded-lg border text-left transition-all text-xs ${
                                  modelConfig.thinking_model === model.id
                                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                }`}
                              >
                                <span className="font-medium">{model.name}</span>
                              </button>
                            ))
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {evaPeopleSaveError && <p role="alert" className="text-sm text-destructive">{evaPeopleSaveError}</p>}
          {evaPeopleSaved && !isEvaPeopleTabDirty && <p role="status" className="text-sm text-muted-foreground">Eva People settings saved.</p>}
          <div className="flex justify-end">
            <Button className="min-h-11" onClick={handleSaveEvaPeopleConfig} disabled={savingEvaPeopleConfig || !evaPeopleConfigLoaded}>
              {savingEvaPeopleConfig ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Eva People Settings
            </Button>
          </div>
          </fieldset>
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
